import { usePostHog } from 'posthog-js/react';

import type {
  CaptureEventProperties,
  EventCategory,
  EventName,
  GroupKey,
  GroupType,
  UserIdentificationProperties,
} from '@shared/analytics';

import {
  parseCaptureProperties,
  parseIdentifyProperties,
  parseGroupProperties,
} from '../../../shared/analytics/parsers';

export type AnalyticsHook = {
  /**
   * Capture an event with optional properties
   * @param eventName - The name of the event to capture
   * @param properties - Optional properties to include with the event
   */
  capture: <TEventCategory extends EventCategory>(
    eventName: EventName<TEventCategory>,
    properties?: CaptureEventProperties<NoInfer<TEventCategory>>,
  ) => void;

  /**
   * Identify a user
   * @param userId - The user ID to identify
   * @param properties - Optional user properties
   */
  identify: (userId: string, properties?: UserIdentificationProperties) => void;

  /**
   * Group a collection of events under a common meta property.
   * @param groupType - The meta type of group (e.g., 'userType' or 'healthCategory')
   * @param groupKey - The group key to identify the events within the meta group
   */
  group: <TGroupType extends GroupType>(groupType: TGroupType, groupKey: GroupKey<NoInfer<TGroupType>>) => void;

  /**
   * Reset the user session.
   *
   * From posthog:
   * > NB reset is normally only called when a user logs out calling reset at the wrong time can lead to unexpected results like split sessions
   * > Resets all info. For example: session id, super properties, sets a random distinct_id and more.
   */
  reset: () => void;

  /**
   * Check if analytics is enabled
   */
  isEnabled: boolean;
};

// Check if we're in development mode without analytics enabled
const isEnabled = !import.meta.env.DEV || !!import.meta.env.VITE_DEV_ENABLE_ANALYTICS;

export const useInternalAnalytics = (): AnalyticsHook => {
  const posthog = usePostHog();

  const capture = <TEventCategory extends EventCategory>(
    eventName: EventName<TEventCategory>,
    properties?: CaptureEventProperties<TEventCategory>,
  ) => {
    if (!isEnabled) {
      return;
    }

    const parsedProperties = parseCaptureProperties(properties);

    posthog?.capture(eventName, parsedProperties);
  };

  const identify = (userId: string, properties?: UserIdentificationProperties) => {
    if (!isEnabled) {
      return;
    }

    const parsedProperties = parseIdentifyProperties(properties);

    posthog?.identify(userId, parsedProperties);
  };

  const group = <TGroupType extends GroupType>(groupType: TGroupType, groupKey: GroupKey<NoInfer<TGroupType>>) => {
    if (!isEnabled) {
      return;
    }

    const { parsedGroupType, parsedGroupKey } = parseGroupProperties({ groupType, groupKey });

    posthog?.group(parsedGroupType, parsedGroupKey);
  };

  const reset = () => {
    if (!isEnabled) {
      return;
    }

    posthog?.reset();
  };

  return {
    capture,
    identify,
    group,
    reset,
    isEnabled,
  };
};
